import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { Input } from 'antd';

import { Languages } from 'context';
import { FormFieldset } from 'organisms/ResumeForm';
import type { ICvTextValue } from 'organisms/CareerProfileForm/types';
import { careerColor } from 'services/careerTokens';
import { careerType } from 'services/careerType';

const { TextArea } = Input;

export interface ICvTextBlock {
  /** Язык документа — выбран один раз в панели над редактором. */
  language: Languages;
  value: ICvTextValue;
  onChange: (field: keyof ICvTextValue, value: string) => void;
  /** Предпросмотр методиста (ТЗ 0039 доводка): текст видно, сохранять некуда. */
  readOnly?: boolean;
}

const LANGUAGE_LABEL: Record<string, string> = {
  [Languages.polish]: 'careerProfile.cvText.languagePolish',
  [Languages.english]: 'careerProfile.cvText.languageEnglish',
};

/**
 * Заголовок CV и «O mnie» — на выбранном языке документа (0072, шаг 2).
 *
 * Раньше блок держал СВОЁ состояние и свой переключатель языка: смена языка
 * засевала поля с сервера и молча стирала несохранённый текст, а сохранялся
 * он отдельной кнопкой. Теперь это поля той же формы: язык выбирается в панели
 * над редактором, черновики обоих языков живут в `cvTexts`, и сохраняет их та
 * же «Zapisz zmiany».
 *
 * «Nagłówek CV» ≠ «Docelowe stanowisko»: первое — строка под именем в документе
 * (пусто — возьмём docelowe stanowisko), второе — по чему подбираются вакансии.
 */
export const CvTextBlock = memo(({ language, value, onChange, readOnly = false }: ICvTextBlock) => {
  const { t } = useTranslation();
  const languageLabel = t(LANGUAGE_LABEL[language] ?? LANGUAGE_LABEL[Languages.polish]);

  return (
    <FormFieldset groupTitle={t('careerProfile.cvText.title')}>
      <Info>{t('careerProfile.cvText.info', { language: languageLabel })}</Info>

      <Field>
        <Label htmlFor="cv-job-title">{t('careerProfile.cvText.jobTitle')}</Label>
        <Input
          id="cv-job-title"
          disabled={readOnly}
          value={value.jobTitle}
          maxLength={150}
          lang={language === Languages.english ? 'en' : 'pl'}
          aria-describedby="cv-job-title-hint"
          placeholder={t('careerProfile.cvText.jobTitlePlaceholder')}
          onChange={({ target }) => onChange('jobTitle', target.value)}
        />
        <Hint id="cv-job-title-hint">{t('careerProfile.cvText.jobTitleHint')}</Hint>
      </Field>

      <Field>
        <Label htmlFor="cv-about-text">{t('careerProfile.cvText.about')}</Label>
        <TextArea
          id="cv-about-text"
          disabled={readOnly}
          value={value.about}
          rows={8}
          maxLength={1500}
          showCount
          lang={language === Languages.english ? 'en' : 'pl'}
          placeholder={t('careerProfile.cvText.aboutPlaceholder')}
          onChange={({ target }) => onChange('about', target.value)}
        />
      </Field>
    </FormFieldset>
  );
});

const Info = styled.p`
  margin: 0 0 16px;
  color: ${careerColor.text2};
  ${careerType.body()};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const Label = styled.label`
  color: ${careerColor.text};
  ${careerType.bodyStrong()};
`;

const Hint = styled.p`
  margin: 0;
  color: ${careerColor.text2};
  ${careerType.meta()};
`;
